<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import type { DisclosureResult, NavLeaf } from '@/services/ai-disclosure/type/ai-disclosure.type';
import IconEyeOff from '@/presentation/components/icons/IconEyeOff.vue';
import { useElementSize } from '@vueuse/core';

const COLLAPSE_NAV_HEIGHT = 240;

const props = withDefaults(
    defineProps<{
        tree: DisclosureResult[];
        showCompleteNav: boolean;
        currentLeaf?: string;
    }>(),
    {
        currentLeaf: '',
    }
);

const emits = defineEmits<{
    (event: 'leafClick', item: NavLeaf): void;
    (event: 'visibleChange', item: NavLeaf): void;
}>();

const activeLeaf = ref<string>(props.currentLeaf || props.tree[0].type);
const cursorStyle = computed(() => {
    const oneLevel = props.tree.filter(item => (!item.parentTitle && !item.isHidden) || item.childrenTypes);
    let idx = oneLevel.findIndex(item => {
        return item.type === props.currentLeaf || item.childrenTypes?.some(child => child === props.currentLeaf);
    });
    if (idx < 0) {
        idx = 0;
    }
    const unitHeight = COLLAPSE_NAV_HEIGHT / (oneLevel.length || 1);

    return {
        height: unitHeight + 'px',
        marginTop: idx * unitHeight + 'px',
    };
});

watch(
    () => props.currentLeaf,
    newVal => {
        activeLeaf.value = newVal;
    }
);

function handleClick(leaf: DisclosureResult) {
    if (leaf.isHidden) return;
    activeLeaf.value = leaf.type;
    emits('leafClick', leaf);
}

function handleParagraphVisibleChange(paragraph: DisclosureResult) {
    emits('visibleChange', paragraph);
}

function getLeafStatus(leaf: DisclosureResult) {
    return leaf.isHidden && !leaf.childrenTypes;
}

const navBarElement = ref<HTMLElement | null>(null);
// @ts-ignore
const { width: navBarElementWidth } = useElementSize(navBarElement);
onMounted(() => {
    nextTick(() => {
        navBarElement.value = document.querySelector('.patsnap-biz-ai-history-nav');
    })
});

const navLeft = computed(() => {
    return {
        left: `${navBarElementWidth.value + 28}px`,
    };
});

</script>

<template>
    <div class="disclosure-nav fixed top-[96px] z-10 flex w-[268px]" :style="navLeft">
        <div
            class="group relative"
            :class="{'h-[240px] w-1 rounded-full bg-grey-40': !showCompleteNav}"
        >
            <div
                v-if="!showCompleteNav"
                class="absolute z-0 w-1 rounded-full bg-primary"
                :style="cursorStyle"
            />

            <div
                :class="[
                    showCompleteNav? 'flex' : 'disclosure-nav-main--inactive',
                ]"
            >
                <div class="disclosure-nav-border ml-1 mt-3 h-[300px] !w-[1px] bg-grey-55" />
                <div>
                    <div
                        v-for="item in props.tree"
                        :key="item.type"
                        :class="[
                            'disclosure-nav__item flex w-[252px] items-center justify-between hover:text-primary',

                        ]"
                        @click="handleClick(item)"
                    >
                        <span
                            :class="[
                                item.parentTitle? 'pl-8' : 'disclosure-nav-point',
                                {
                                    'text-grey-60':getLeafStatus(item)
                                },
                                {
                                    'disclosure-nav__item-active text-primary': activeLeaf === item.type,
                                }
                            ]"
                        >
                            {{ item.title }}
                        </span>

                        <IconEyeOff
                            v-if="getLeafStatus(item)"
                            class="th-btn-icon mr-1 h-4 w-4 cursor-pointer text-grey-60"
                            @click.native="handleParagraphVisibleChange(item)"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.disclosure-nav {
    &-point {
        @apply items-center flex gap-2;

        &::before {
            content: '';
            display: inline-block;

            @apply w-[9px] h-[9px] bg-white border border-grey-55 rounded-full;
            &:hover {
                @apply border-primary;
            }
        }

        &.disclosure-nav__item-active {
            &::before {
                @apply bg-primary border-primary;
            }
        }
    }

    &__item {
        @apply py-1.5 cursor-pointer -ml-[5px];
    }

    &-main--inactive {
        box-shadow: 0 2px 6px 0 #00000014;
        @apply absolute z-10 hidden w-[268px] bg-white p-2 group-hover:flex;
    }
}
</style>
